import React, { useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  CalendarMonth,
  CheckCircleOutline,
  Edit,
  HistoryOutlined,
} from "@mui/icons-material";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { designColors, designShell } from "services/constants/designTokens";
import { CorrectionApplyPanel } from "../CorrectionShared/CorrectionApplyPanel";
import { correctionMeta } from "./attendanceCorrectionHelpers";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";

const formatStatusDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["DD-MM-YYYY, HH:mm", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY, HH:mm") : value;
};

const detailAccent = {
  date: detailAccentPalette.indigo,
  type: detailAccentPalette.pink,
  earlyExit: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
};

const punchHeadCell = {
  py: 0.6,
  px: 1,
  border: "none",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: designColors.slate500,
};

const punchBodyCell = {
  py: 0.7,
  px: 1,
  border: "none",
  fontSize: 12.5,
  color: designColors.slate800,
};

const formatEntryDate = (entry, fallbackDate) => {
  if (entry?.date) {
    const parsed = moment(entry.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
    if (parsed.isValid()) return parsed.format("DD-MM-YYYY");
  }
  if (entry?.punch_datetime) {
    const parsed = moment(entry.punch_datetime);
    if (parsed.isValid()) return parsed.format("DD-MM-YYYY");
  }
  return fallbackDate || "-";
};

const formatEntryTime = (time) => {
  const parsed = moment(time, ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : time || "-";
};

const directionStyle = (direction) =>
  String(direction || "").toUpperCase() === "IN"
    ? detailAccentPalette.green
    : detailAccentPalette.orange;

const PunchEntriesTable = ({ title, icon: Icon, tint, entries = [], fallbackDate }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 16, color: tint }} />
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: designColors.slate700 }}>
        {title}
      </Typography>
    </Stack>
    {entries.length === 0 ? (
      <Typography sx={{ fontSize: 12.5, color: designColors.slate500, py: 0.5 }}>
        No punches
      </Typography>
    ) : (
      <Table size="small" sx={{ "& tr:last-child td": { pb: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={punchHeadCell}>Date</TableCell>
            <TableCell sx={punchHeadCell}>Time</TableCell>
            <TableCell sx={punchHeadCell}>Direction</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry, index) => {
            const visual = directionStyle(entry?.direction);
            return (
              <TableRow key={entry?.punch_id ?? entry?.rowId ?? index}>
                <TableCell sx={punchBodyCell}>{formatEntryDate(entry, fallbackDate)}</TableCell>
                <TableCell sx={punchBodyCell}>{formatEntryTime(entry?.time)}</TableCell>
                <TableCell sx={punchBodyCell}>
                  <Chip
                    size="small"
                    label={String(entry?.direction || "-").toUpperCase()}
                    sx={{
                      height: 20,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: visual.color,
                      backgroundColor: visual.bg,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    )}
  </Box>
);

export const ViewAttendanceCorrection = ({
  open = true,
  selectedData,
  onClose,
  onUpdated,
  checkAccess,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Edit and Cancel used to be gated on status alone, so a view-only user
  // could open either from this panel with no permission check at all (the
  // New Request button on AttendanceCorrection/index.js already gates
  // creation on this same checkAccess). Both now require it too, hiding
  // rather than disabling the action for a viewer who can't use it.
  const canActOn = checkAccess && selectedData?.status === "Pending";
  const displayData = normalizeApprovalPersonFields({
    ...selectedData,
    employee_name:
      selectedData?.employee_name ||
      selectedData?.user_name ||
      selectedData?.username ||
      userDetails?.first_name,
    employee_id: selectedData?.employee_id || selectedData?.emp_id || userDetails?.employee_id,
  });
  const employeeName = displayData.employee_name || "N/A";
  const employeeId =
    selectedData?.employee_id ||
    selectedData?.emp_id ||
    userDetails?.employee_id ||
    "-";
  const department =
    selectedData?.department_name ||
    selectedData?.department ||
    userDetails?.department_name ||
    "-";
  const designation =
    selectedData?.designation_name ||
    selectedData?.designation ||
    userDetails?.designation ||
    "-";
  const statusFlowData = {
    ...selectedData,
    applied_by: displayData.applied_by || selectedData?.createdby || employeeName,
    applied_on: formatStatusDateTime(
      selectedData?.applied_on ||
        selectedData?.createdat ||
        selectedData?.created_at ||
        selectedData?.created_date,
    ),
    approved_by: displayData.approved_by,
    approved_at: formatStatusDateTime(
      selectedData?.approved_at || selectedData?.approved_on,
    ),
    rejected_by: displayData.rejected_by,
    rejected_at: formatStatusDateTime(
      selectedData?.rejected_at || selectedData?.rejected_on,
    ),
    cancelled_by: displayData.cancelled_by,
    cancelled_at: formatStatusDateTime(
      selectedData?.cancelled_at || selectedData?.cancelled_on,
    ),
  };

  const handleCancelRequest = async () => {
    setCancelling(true);
    const response = await customAPI(
      `${domain}my_zone/apply/attendance_correction/myzone-attendance-correction/?company_id=${requestCompanyId}`,
      "PUT",
      {
        user_id: selectedData?.user_id,
        date: selectedData?.date,
        status: "Cancelled",
      },
    );
    setCancelling(false);
    setShowCancelDialog(false);
    if (response?.ok) {
      showToast("success", "Attendance correction request cancelled.");
      onUpdated?.();
      onClose();
    } else {
      showToast("error", response?.message);
    }
  };

  // selectedData.date is "YYYY-MM-DD"; show DD-MM-YYYY like the grid does.
  const arDate = moment(selectedData?.date, "YYYY-MM-DD", true);
  const fallbackDate = arDate.isValid()
    ? arDate.format("DD-MM-YYYY")
    : selectedData?.date || "-";
  const originalEntries = selectedData?.original_entries || [];
  const requestedEntries = selectedData?.entries || [];

  const editAction = canActOn ? (
    <Tooltip title="Edit" arrow>
      <IconButton
        onClick={() => setIsEditOpen(true)}
        sx={{
          ...designShell.panelSoftIconButton,
          color: designColors.slate500,
        }}
      >
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Attendance Correction"
      subtitle="Request details and status"
      headerActions={editAction}
      footer={
        canActOn ? (
          <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
        ) : null
      }
    >
      <ConfirmationDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelRequest}
        title="Cancel Request"
        message="Are you sure you want to cancel this request?"
        confirmText="Confirm"
        cancelText="Cancel"
        disableAction={cancelling}
      />
      {isEditOpen ? (
        <CorrectionApplyPanel
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onCreate={() => {
            onUpdated?.();
            onClose();
          }}
          title="Edit Attendance Correction"
          subtitle="Correct missing, incorrect or wrong punch entries for this day."
          editRecord={selectedData}
        />
      ) : null}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: selectedData?.image_url || userDetails?.image_url,
            employee_id: employeeId,
            user_name: employeeName,
            department_name: department,
            designation,
          }}
          status={selectedData?.status}
          colorCode={selectedData?.color_code}
          variant="employeeDetailsCard"
        />

        <Box sx={detailCardSx}>
          <Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
            Correction Details
          </Typography>
          <RequestDetailField
            icon={CalendarMonth}
            label="AR Date"
            value={fallbackDate}
            accent={detailAccent.date}
          />
          <RequestDetailField
            icon={EditCalendarOutlinedIcon}
            label="Correction Type"
            value={correctionMeta(selectedData?.correction_type).label}
            accent={detailAccent.type}
          />
          <RequestDetailField
            icon={CheckCircleOutline}
            label="Early Exit"
            value={selectedData?.early_exit ? "Yes" : "No"}
            accent={detailAccent.earlyExit}
            isLast
          />

          <Divider sx={{ my: 1.6, borderColor: designColors.slate100 }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: designColors.slate500,
              mb: 1.25,
            }}
          >
            PUNCH DETAILS
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
            <PunchEntriesTable
              title="Current Punches"
              icon={HistoryOutlined}
              tint={designColors.slate500}
              entries={originalEntries}
              fallbackDate={fallbackDate}
            />
            <PunchEntriesTable
              title="Requested Punches"
              icon={AccessTime}
              tint={detailAccentPalette.green.color}
              entries={requestedEntries}
              fallbackDate={fallbackDate}
            />
          </Stack>

          <Divider sx={{ my: 1.6, borderColor: designColors.slate100 }} />
          <RequestDetailField
            icon={DescriptionOutlinedIcon}
            label="Reason"
            value={selectedData?.reason || "-"}
            accent={detailAccent.reason}
            multiline
            isLast
          />
        </Box>

        <RequestStatusTimeline
          endDate={selectedData?.date}
          data={statusFlowData}
          hidePendingStep
        />
        <ApprovalEditAuditTrail data={selectedData} />

      </Box>
    </GlobalSidePanel>
  );
};

export default ViewAttendanceCorrection;
